import React, { useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Grid,
  GridItem,
  Text,
  Link,
  HStack,
  Heading,
  Flex,
} from '@chakra-ui/react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { FiFileText, FiArrowRight } from 'react-icons/fi';
import { getPlatform } from '../content/platforms';
import { useLegalLang } from '../LegalLangContext';
import { APP_META } from '../content/appMeta';
import PlatformHero from '../components/PlatformHero';
import TableOfContents from '../components/TableOfContents';
import { LegalSection, LegalParagraph, LegalList } from '../components/LegalSection';
import PlatformPolicyList from '../components/PlatformPolicyList';
import PlatformNotFound from '../components/PlatformNotFound';

const SECTION_KEYS = [
  'p_collected',
  'p_use',
  'p_share',
  'p_store',
  'p_retain',
  'p_rights',
  'p_children',
  'p_international',
  'p_changes',
  'p_contact',
];

const PrivacyPage = ({ platformOverride }) => {
  const { platform: platformSlug } = useParams();
  const platform = getPlatform(platformOverride || platformSlug);
  const { t, lang } = useLegalLang();

  useEffect(() => {
    if (platform) {
      document.title = platformOverride
        ? `${t('privacyDocTitle')} | ${APP_META.name}`
        : `${t('privacyDocTitle')} — ${platform.name} | ${APP_META.name}`;
    } else {
      document.title = `${t('notFoundTitle')} | ${APP_META.name}`;
    }
  }, [platform, lang, t, platformOverride]);

  const tocItems = useMemo(
    () =>
      SECTION_KEYS.map(key => ({
        id: key.replace(/^p_/, 'sec-'),
        label: t(key),
      })),
    [t, lang],
  );

  if (!platform) {
    return <PlatformNotFound />;
  }

  const dataAccessed = platform.dataAccessed[lang] || platform.dataAccessed.en;
  const purposeText = platform.purpose[lang] || platform.purpose.en;

  return (
    <Box as="article">
      <PlatformHero platform={platform} docTitle={t('privacyDocTitle')} />

      <Container maxW="6xl" px={{ base: 4, md: 6 }} pb={12}>
        <Grid templateColumns={{ base: '1fr', lg: '220px 1fr' }} gap={{ base: 4, lg: 10 }}>
          <GridItem display={{ base: 'none', lg: 'block' }}>
            <TableOfContents items={tocItems} />
          </GridItem>

          <GridItem>
            <Box
              bg="surface.base"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="xl"
              px={{ base: 5, md: 8 }}
              py={{ base: 6, md: 8 }}
              boxShadow="0 1px 0 rgba(0,0,0,0.02)"
            >
              {/* Intro */}
              <Box pb={2}>
                <LegalParagraph fontSize={{ base: 'md', md: 'lg' }} color="text.primary">
                  {t('privacyIntroBeforePlatform')}{' '}
                  <Text as="span" fontWeight="700">
                    {platform.name}
                  </Text>{' '}
                  {t('privacyIntroAfterPlatform')}
                </LegalParagraph>
              </Box>

              <LegalSection id="sec-collected" title={t('p_collected')}>
                <LegalParagraph>{t('p_collected_intro')}</LegalParagraph>
                <LegalList items={dataAccessed} />
                <Text fontSize="sm" color="text.muted" mt={2} fontStyle="italic">
                  {lang === 'vi' ? 'Mục đích sử dụng' : 'Purpose'}: {purposeText}
                </Text>
              </LegalSection>

              <LegalSection id="sec-use" title={t('p_use')}>
                <LegalParagraph>{t('p_use_body')}</LegalParagraph>
                {platform.extraNotes && (
                  <Box mt={2}>
                    {(platform.extraNotes[lang] || platform.extraNotes.en).map((note, i) => (
                      <LegalParagraph key={i} fontSize="sm" color="text.secondary">
                        {note}
                      </LegalParagraph>
                    ))}
                  </Box>
                )}
              </LegalSection>

              <LegalSection id="sec-share" title={t('p_share')}>
                <LegalParagraph>{t('p_share_body')}</LegalParagraph>
                <Text fontSize="sm" color="text.muted" mt={2}>
                  {lang === 'vi' ? 'Tài liệu liên quan từ nền tảng' : 'Related platform policies'}:
                </Text>
                <PlatformPolicyList links={platform.platformPolicyLinks} />
              </LegalSection>

              <LegalSection id="sec-store" title={t('p_store')}>
                <LegalParagraph>{t('p_store_body')}</LegalParagraph>
              </LegalSection>

              <LegalSection id="sec-retain" title={t('p_retain')}>
                <LegalParagraph>{t('p_retain_body')}</LegalParagraph>
              </LegalSection>

              <LegalSection id="sec-rights" title={t('p_rights')}>
                <LegalParagraph>{t('p_rights_body')}</LegalParagraph>
              </LegalSection>

              <LegalSection id="sec-children" title={t('p_children')}>
                <LegalParagraph>{t('p_children_body')}</LegalParagraph>
              </LegalSection>

              <LegalSection id="sec-international" title={t('p_international')}>
                <LegalParagraph>{t('p_international_body')}</LegalParagraph>
              </LegalSection>

              <LegalSection id="sec-changes" title={t('p_changes')}>
                <LegalParagraph>{t('p_changes_body')}</LegalParagraph>
              </LegalSection>

              <LegalSection id="sec-contact" title={t('p_contact')}>
                <LegalParagraph>
                  {t('p_contact_body')}{' '}
                  <Link
                    href={`mailto:${APP_META.contactEmail}`}
                    color="brand.700"
                    fontWeight="600"
                    _hover={{ textDecoration: 'underline' }}
                  >
                    {APP_META.contactEmail}
                  </Link>
                  .
                </LegalParagraph>
              </LegalSection>
            </Box>

            {/* Cross-link to terms */}
            <Box
              mt={6}
              p={5}
              bg="brand.50"
              border="1px solid"
              borderColor="brand.100"
              borderRadius="xl"
            >
              <HStack justify="space-between" align="center" flexWrap="wrap" gap={3}>
                <HStack spacing={3}>
                  <Flex
                    boxSize="40px"
                    borderRadius="10px"
                    bg="white"
                    color="brand.600"
                    align="center"
                    justify="center"
                  >
                    <FiFileText size={18} />
                  </Flex>
                  <Box>
                    <Text fontWeight="700" color="text.primary">
                      {t('termsDocTitle')} — {platform.name}
                    </Text>
                    <Text fontSize="sm" color="text.secondary">
                      {t('termsIntroBeforePlatform')} {platform.name}{' '}
                      {t('termsIntroAfterPlatform')}
                    </Text>
                  </Box>
                </HStack>
                <Link
                  as={RouterLink}
                  to={`/legal/${platform.slug}/terms`}
                  color="brand.700"
                  fontWeight="600"
                  display="inline-flex"
                  alignItems="center"
                  gap={1}
                  _hover={{ textDecoration: 'underline' }}
                >
                  {t('viewTerms')} <FiArrowRight size={14} />
                </Link>
              </HStack>
            </Box>
          </GridItem>
        </Grid>
      </Container>
    </Box>
  );
};

export default PrivacyPage;
